import { SidebarType } from '../components/layout/sidebar/sidebar.types';
import { Provider } from '../../modules/chat/components/chat.type';

/**
 * Filters a navigation tree to keep only objects with matching parameters in the provided list
 * 
 * This function filters a navigation tree structure to retain only the routes with parameters
 * matching those in the provided list. It also preserves various special paths like "/console/soon",
 * HTTP/HTTPS URLs, and others.
 * 
 * @param {Array} objArray - Array of objects to filter
 * @param {Array<string>} parameterList - List of parameter values to keep
 * @returns {Array} - Filtered array
 */
function filterObjectTree(objArray, parameterList) {
  if (!parameterList || !Array.isArray(parameterList) || parameterList.length === 0) {
    return JSON.parse(JSON.stringify(objArray)); // Return a deep clone if no parameters
  }
  
  // For other cases, use a more general approach
  return filterForParameters(objArray, parameterList);
}

/**
 * More general approach for filtering the tree based on parameter list
 * 
 * @param {Array} objArray - Input array to filter
 * @param {Array<string>} parameterList - List of parameter values to keep
 * @returns {Array} - Filtered array
 */
function filterForParameters(objArray, parameterList) {
  // Start with a deep clone to avoid modifying the input
  const result = JSON.parse(JSON.stringify(objArray));
  
  // Process all nodes to filter chat routes
  for (const obj of result) {
    // if (obj.hasOwnProperty('provider')) {
    //   console.log("filterChatRoutes Processing object:", obj.name);
    // }

    filterChatRoutes(obj, parameterList);
  }
  
  // console.log("filterChatRoutes Filtered object tree:", result);
  return result;
}

/**
 * Recursively filters chat routes in the object tree
 * 
 * @param {Object} obj - The current object to process
 * @param {Array<string>} parameterList - List of parameter values to keep
 */
function filterChatRoutes(obj, parameterList) {
  // If the object has children, process them recursively
  if (obj.children && Array.isArray(obj.children)) {
    // First, filter out chat routes that don't match our parameter list
    const keptChildren: any = [];
    
    for (const child of obj.children) {
      // Special case for /console/chat routes with parameters
      if (child.path === "/console/chat" && child.hasParameter && child.parameter) {

        // Only keep chat routes with parameters in our list
        if (parameterList.includes(child.parameter.toLowerCase())) {
          keptChildren.push(child);
          // console.log("filterChatRoutes Keeping chat route with parameter:", child.parameter);
        } else {
          // console.log("filterChatRoutes Skipping chat route with parameter:", child.parameter);
        }
        // Skip to next child - we've handled this one
        continue;
      }
      
      // For all other paths, keep and process recursively
      keptChildren.push(child);
      filterChatRoutes(child, parameterList);
    }
    
    obj.children = keptChildren;
  }
}

/**
 * Recursively removes all /console/chat routes from the navigation tree
 * 
 * @param {SidebarType[]} items - Array of sidebar items to process
 * @returns {SidebarType[]} - Filtered array with chat routes removed
 */
function pruneConsoleChat(items: SidebarType[]): SidebarType[] {
  return items.reduce((acc: SidebarType[], item) => {
    // Skip items with path /console/chat
    if (item.path === '/console/chat') {
      return acc;
    }

    // Clone the item to avoid modifying the original
    const newItem = { ...item };
    
    // Recursively process children if they exist
    if (newItem.children && Array.isArray(newItem.children)) {
      newItem.children = pruneConsoleChat(newItem.children);
      
      // If children array is empty after pruning, skip this item entirely
      if (newItem.children.length === 0) {
        return acc;
      }
    }
    
    acc.push(newItem);
    return acc;
  }, []);
}

export const filterSidebarByProviders = (
  sidebarItems: SidebarType[],
  otherRoutes: SidebarType[],
  providers: Provider[]
): SidebarType[] => {

  if (providers.length === 0) {
    // console.warn("filterChatRoutes No providers found, returning original sidebar items without filtering.");
    const filteredOtherRoutes = pruneConsoleChat(otherRoutes);
    return [...sidebarItems, ...filteredOtherRoutes];
  } else {
    // console.log("filterChatRoutes sidebar items based on providers:", providers.map(p => p.name.toLowerCase()));

    // Filter the otherRoutes based on providers
    const filteredOtherRoutes = filterObjectTree(otherRoutes, providers.map(p => p.name.toLowerCase()));

    // console.log("filterChatRoutes Filtered other routes:", filteredOtherRoutes);
    
    // Combine the original sidebarItems with the filtered otherRoutes
    return [...sidebarItems, ...filteredOtherRoutes];
  }
};